import React from 'react';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent } from '../services/analyticsService';

interface SidebarProps {
  onNavigate: (page: string) => void;
  achievementCount: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = ({ onNavigate, achievementCount, isCollapsed, onToggleCollapse, activePage }) => {
  const { signOut, user, profile, isDemoMode } = useUser();
  const { t } = useLanguage();

  const handleNavigation = (page: string) => {
    onNavigate(page);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      onToggleCollapse();
    }
  };

  const handleExitDemo = () => {
    // Show warning about losing progress
    const confirmed = window.confirm(
      '⚠️ WARNING: You are about to exit the demo.\n\n' +
      'All your demo progress will be LOST.\n\n' +
      'Sign up to start your real journey!\n\n' +
      'Are you sure you want to continue?'
    );
    
    if (!confirmed) {
      return; // User cancelled, don't exit
    }
    
    // Track demo session end with duration
    const demoUserStr = sessionStorage.getItem('demo_user');
    if (demoUserStr) {
      const demoUser = JSON.parse(demoUserStr);
      const sessionDuration = Date.now() - demoUser.createdAt;
      const sessionDurationMinutes = Math.floor(sessionDuration / (1000 * 60));
      
      trackEvent('demo_session_ended', {
        session_duration_minutes: sessionDurationMinutes,
        demo_user_level: demoUser.profile.level,
        demo_quests_completed: demoUser.quests.filter((q: any) => q.status === 'completed').length,
        exit_reason: 'user_exit',
        demo_engagement: sessionDurationMinutes > 30 ? 'high' : 'low'
      });
    }
    
    // Clear demo data from session storage
    sessionStorage.removeItem('is_demo_mode');
    sessionStorage.removeItem('demo_user');
    
    // Clear demo background settings from localStorage
    localStorage.removeItem('demo_background_settings');
    
    // Navigate to landing page
    window.location.href = '/';
  };

  const menuItems = [
    { id: 'quests', iconPath: '/icons/icon-quest-main.png', label: t.nav.quests.toUpperCase() },
    { id: 'focus', iconPath: '/icons/icon-page-focus.png', label: t.nav.focus.toUpperCase() },
    { id: 'passiveSkills', iconPath: '/icons/icon-programs.png', label: t.nav.passiveSkills.toUpperCase() },
    { id: 'calendar', iconPath: '/icons/icon-page-calendar.png', label: t.nav.calendar.toUpperCase() },
    { id: 'shop', iconPath: '/icons/icon-shop.png', label: t.nav.shop.toUpperCase() },
    { id: 'stats', iconPath: '/icons/icon-statistics.png', label: t.nav.stats.toUpperCase() },
    { id: 'history', iconPath: '/icons/icon-page-history.png', label: t.nav.history.toUpperCase() },
    { id: 'achievements', iconPath: '/icons/icon-premium.png', label: t.nav.achievements.toUpperCase(), badge: achievementCount },
    { id: 'settings', iconPath: '/icons/icon-settings.png', label: t.nav.settings.toUpperCase() },
  ];

  return (
    <>
      {/* Sidebar */}
      <div 
        data-onboarding="sidebar"
        className={`fixed inset-y-4 left-4 z-[50] flex flex-col transition-all duration-300 overflow-hidden rounded-3xl border-2 border-slate-600/40 shadow-[0_8px_24px_rgba(0,0,0,0.6),0_2px_8px_rgba(0,0,0,0.4)] ${
          isCollapsed 
            ? '-translate-x-full lg:translate-x-0 lg:w-20 opacity-0 pointer-events-none lg:opacity-100 lg:pointer-events-auto'
            : 'translate-x-0 w-64 opacity-100 pointer-events-auto'
        }`}
        style={{
          WebkitBackdropFilter: 'blur(4px)',
          backdropFilter: 'blur(4px)',
          // Match StatsPanel glass gradient
          background: 'linear-gradient(180deg, rgba(30,41,59,0.40) 0%, rgba(15,23,42,0.40) 50%, rgba(2,6,23,0.40) 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.1), inset 0 -3px 8px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-600/30">
          <div className={`flex items-center gap-3 transition-all duration-300 ${isCollapsed ? 'hidden' : 'flex'}`}>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse flex-shrink-0"></div>
            <span className="font-orbitron text-sm tracking-wider text-blue-400 uppercase whitespace-nowrap">
              MENU
            </span>
          </div>
          <button
            onClick={onToggleCollapse}
            className="p-2 hover:bg-slate-700/30 rounded transition-all"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg 
              className={`w-5 h-5 text-blue-400 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
            </svg>
          </button>
        </div>

        {/* User Info */}
        <div className={`p-4 border-b border-slate-600/30 transition-all duration-300 overflow-hidden ${isCollapsed ? 'h-0 p-0 opacity-0' : 'h-auto p-4 opacity-100'}`}>
          <div className="flex items-center gap-3">
            {profile?.avatar ? (
              <div className="relative flex-shrink-0">
                <img
                  src={profile.avatar}
                  alt="User Avatar"
                  className="w-10 h-10 object-cover rounded-full border-2 border-slate-500/50"
                />
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center font-orbitron font-bold text-lg flex-shrink-0 border border-blue-500/50">
                {user?.displayName?.[0]?.toUpperCase() || 'H'}
              </div>
            )}
            <div className={`transition-all duration-300 ${isCollapsed ? 'hidden' : 'block'}`}>
              <div className="text-sm font-orbitron text-white font-bold truncate max-w-[140px] whitespace-nowrap">
                {profile?.name || user?.displayName || 'Hunter'}
              </div>
              <div className="text-xs text-gray-400 font-rajdhani truncate max-w-[140px] whitespace-nowrap">
                {user?.email}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="p-3 space-y-1.5 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = activePage === item.id;
            const baseClasses = 'relative w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl transition-all group border backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]';
            
            const borderClasses = isActive 
              ? 'bg-gradient-to-br from-blue-500/15 via-blue-400/10 to-cyan-400/10 border-blue-400/40 ring-1 ring-blue-400/30 shadow-[0_8px_20px_rgba(59,130,246,0.25)]'
              : 'bg-slate-700/25 hover:bg-slate-700/35 border-slate-600/40 hover:border-slate-500/60 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5';

            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.id)}
                className={`${baseClasses} ${borderClasses} ${isCollapsed ? 'justify-center' : 'justify-start'}`}
                title={isCollapsed ? item.label : ''}
              >
                {/* Active left glow accent */}
                {isActive && !isCollapsed && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-[3px] rounded-full bg-gradient-to-b from-blue-400 to-cyan-400 shadow-[0_0_10px_rgba(59,130,246,0.7)]" />
                )}
                {/* Icon */}
                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                  {item.iconPath ? (
                    <img 
                      src={item.iconPath} 
                      alt={item.label}
                      className="w-6 h-6 object-contain transition-transform duration-200 group-hover:scale-110"
                      style={{ 
                        filter: isActive 
                          ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.8))' 
                          : 'none'
                      }}
                    />
                  ) : (
                    <span className="text-xl">🏷️</span>
                  )}
                </div>
                
                {/* Label */}
                <span className={`font-orbitron font-semibold text-xs tracking-wider transition-all duration-300 whitespace-nowrap ${isCollapsed ? 'hidden' : 'inline'} ${isActive ? 'text-blue-400 drop-shadow-[0_0_6px_rgba(59,130,246,0.6)]' : 'text-white'}`}>
                  {item.label}
                </span>

                {/* Badge */}
                {item.badge && item.badge > 0 && (
                  <span className={`absolute ${isCollapsed ? '-top-1 -right-1' : 'right-2 top-1/2 -translate-y-1/2'} bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg shadow-blue-500/50`}>
                    {item.badge}
                  </span>
                )}

                {/* Hover effect overlay */}
                <div className="absolute inset-0 rounded-2xl bg-white/2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            );
          })}
        </nav>

        {/* Sign Out / Exit Demo Button */}
        <div className="p-3 border-t border-slate-600/30">
          <button
            onClick={isDemoMode ? handleExitDemo : signOut}
            className={`relative w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl transition-all group border backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ${isDemoMode 
              ? 'bg-gradient-to-br from-yellow-500/15 via-yellow-400/10 to-orange-400/10 border-yellow-500/40 hover:border-yellow-500/60 hover:shadow-lg hover:shadow-yellow-500/20 hover:-translate-y-0.5 ring-1 ring-yellow-400/30' 
              : 'bg-gradient-to-br from-red-500/15 via-red-400/10 to-rose-400/10 border-red-500/40 hover:border-red-500/60 hover:shadow-lg hover:shadow-red-500/20 hover:-translate-y-0.5 ring-1 ring-red-400/30'
            } ${isCollapsed ? 'justify-center' : 'justify-start'}`}
            title={isCollapsed ? (isDemoMode ? 'Exit Demo' : t.nav.signOut) : ''}
          >
            {/* Hover effect overlay */}
            <div className="absolute inset-0 rounded-2xl bg-white/2 opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative w-6 h-6 flex items-center justify-center flex-shrink-0">
              <img 
                src="/icons/icon-close.png" 
                alt={isDemoMode ? "Exit Demo" : "Sign Out"}
                className="w-6 h-6 object-contain transition-transform duration-200 group-hover:scale-110"
                style={{ 
                  filter: isDemoMode 
                    ? 'drop-shadow(0 0 6px rgba(234, 179, 8, 0.8))' 
                    : 'drop-shadow(0 0 6px rgba(239, 68, 68, 0.8))' 
                }}
              />
            </div>
            <span className={`relative font-orbitron font-semibold text-xs tracking-wider transition-all duration-300 whitespace-nowrap ${isCollapsed ? 'hidden' : 'inline'} ${isDemoMode ? 'text-yellow-400 drop-shadow-[0_0_6px_rgba(234,179,8,0.6)]' : 'text-red-400 drop-shadow-[0_0_6px_rgba(239,68,68,0.6)]'}`}>
              {isDemoMode ? 'EXIT DEMO' : t.nav.signOut.toUpperCase()}
            </span>
          </button>
        </div>

        {/* Bottom Decorative Element */}
        <div className="p-4 border-t border-slate-600/30">
          <div className="flex items-center gap-2 justify-center opacity-50">
            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-[45] lg:hidden"
          style={{
            WebkitBackdropFilter: 'blur(10px)',
            backdropFilter: 'blur(10px)',
          }}
          onClick={onToggleCollapse}
        />
      )}
    </>
  );
};

export default Sidebar;
